import {
  useSendApiErrorNotification,
  useSendNotification,
} from "@app/hooks/useNotification";
import { clientFetch } from "@app/lib/egress/client";
import { useDataSourceViewContentNodes } from "@app/lib/swr/data_source_views";
import {
  getErrorFromResponse,
  useFetcher,
  useSWRWithDefaults,
} from "@app/lib/swr/swr";
import type { GetDataSourceViewDocumentResponseBody } from "@app/types/api/data_source_view";
import type {
  PatchDocumentResponseBody,
  PostDocumentResponseBody,
} from "@app/types/api/data_sources";
import type {
  PatchDataSourceDocumentRequestBody,
  PostDataSourceDocumentRequestBody,
} from "@app/types/api/public/data_sources";
import type { DataSourceViewType } from "@app/types/data_source_view";
import type { LightWorkspaceType } from "@app/types/user";
import { useLingui } from "@lingui/react/macro";
import type { Fetcher } from "swr";

export function useDataSourceViewDocument({
  dataSourceView,
  documentId,
  owner,
  disabled,
}: {
  dataSourceView: DataSourceViewType | null;
  documentId: string | null;
  owner: LightWorkspaceType;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const dataSourceViewDocumentFetcher: Fetcher<GetDataSourceViewDocumentResponseBody> =
    fetcher;
  const url =
    dataSourceView && documentId
      ? `/api/w/${owner.sId}/spaces/${dataSourceView.spaceId}/data_source_views/` +
        `${dataSourceView.sId}/documents/${encodeURIComponent(documentId)}`
      : null;

  const { data, error, mutate } = useSWRWithDefaults(
    url,
    dataSourceViewDocumentFetcher,
    {
      disabled,
    }
  );

  return {
    document: data?.document,
    isDocumentLoading: !disabled && !error && !data,
    isDocumentError: error,
    mutateDocument: mutate,
  };
}

export function useUpdateDataSourceViewDocument(
  owner: LightWorkspaceType,
  dataSourceView: DataSourceViewType,
  documentId: string
) {
  const { mutateRegardlessOfQueryParams: mutateContentNodes } =
    useDataSourceViewContentNodes({
      owner,
      dataSourceView,
      disabled: true, // Needed just to create
    });

  // Used only for cache invalidation
  const { mutateDocument } = useDataSourceViewDocument({
    owner,
    dataSourceView,
    documentId,
    disabled: true, // Needed just to create
  });

  const { t } = useLingui();
  const sendApiErrorNotification = useSendApiErrorNotification();
  const sendNotification = useSendNotification();

  const doUpdate = async (body: PatchDataSourceDocumentRequestBody) => {
    const patchUrl =
      `/api/w/${owner.sId}/spaces/${dataSourceView.spaceId}/` +
      `data_sources/${dataSourceView.dataSource.sId}/documents/${encodeURIComponent(documentId)}`;
    const res = await clientFetch(patchUrl, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const errorData = await getErrorFromResponse(res);
      sendApiErrorNotification({
        title: t`Failed to update document`,
        error: errorData,
      });
      return null;
    } else {
      void mutateContentNodes();
      void mutateDocument();

      sendNotification({
        type: "success",
        title: t`Document updated`,
        description: t`Document has been updated.`,
      });

      const response: PatchDocumentResponseBody = await res.json();
      return response.document;
    }
  };
  return doUpdate;
}

export function useCreateDataSourceViewDocument(
  owner: LightWorkspaceType,
  dataSourceView: DataSourceViewType
) {
  // Used only for cache invalidation
  const { mutateRegardlessOfQueryParams: mutateContentNodes } =
    useDataSourceViewContentNodes({
      owner,
      dataSourceView,
      disabled: true,
    });

  const { t } = useLingui();
  const sendApiErrorNotification = useSendApiErrorNotification();
  const sendNotification = useSendNotification();

  const doCreate = async (body: PostDataSourceDocumentRequestBody) => {
    const createUrl =
      `/api/w/${owner.sId}/spaces/${dataSourceView.spaceId}/` +
      `data_sources/${dataSourceView.dataSource.sId}/documents`;
    const res = await clientFetch(createUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const errorData = await getErrorFromResponse(res);
      sendApiErrorNotification({
        title: t`Failed to create document`,
        error: errorData,
      });
      return null;
    } else {
      void mutateContentNodes();

      sendNotification({
        type: "success",
        title: t`Document processing`,
        description: t`Your document is processing and will appear shortly.`,
      });

      const response: PostDocumentResponseBody = await res.json();
      return response.document;
    }
  };

  return doCreate;
}
